import DeleteIcon from "@mui/icons-material/Delete";
import {
  Alert,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  useTheme,
} from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import {
  AlertDialog,
  RobotoButton,
  RobotoTypography,
} from "@/shared/components";
import { EntityCustomFields } from "@/shared/components/CustomFields";
import {
  DatasetDevice,
  UpdateDatasetMetadata,
  UpdateDatasetTags,
} from "@/shared/components/datasets";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import { FormattedDateTime } from "@/shared/datetime";
import { TargetEntityType } from "@/shared/domain/customFields";
import { useDataset, useUpdateDataset } from "@/shared/domain/datasets/hooks";

import { useDatasetContext } from "../useDatasetContext";

/**
 * Renders the "Details" tab of a dataset: created/modified timestamps, owning
 * device, tags, metadata, custom fields, and the destructive "Delete" operation.
 */
export const DatasetDetails: React.FC = () => {
  const { datasetId, org: currentOrg } = useDatasetContext();
  const { data: dataset } = useDataset(datasetId);
  const theme = useTheme();
  const [alertDialogOpen, setAlertDialogOpen] = React.useState<boolean>(false);
  const [alertDialogText, setAlertDialogText] = React.useState<string>("");
  const [alertDialogAction, setAlertDialogAction] =
    React.useState<() => Promise<void>>();
  const [error, setError] = React.useState<Error | null>(null);
  const { datasetService } = useDomainServices();
  const { mutateAsync: updateDataset } = useUpdateDataset();
  const { goto } = useNavigation();

  function wrapActionWithDialog(text: string, action: () => Promise<void>) {
    setAlertDialogText(text);
    setAlertDialogAction(() => action);
    setAlertDialogOpen(true);
  }

  if (dataset !== undefined) {
    return (
      <>
        <div>
          {error !== null && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error.message}
            </Alert>
          )}
          <List sx={{ width: "100%", padding: 0 }}>
            <ListItem
              sx={{
                paddingLeft: 0.5,
              }}
            >
              <ListItemText
                primary={"Created"}
                primaryTypographyProps={{
                  variant: "body2",
                }}
                secondary={<FormattedDateTime value={dataset.created || ""} />}
              />
            </ListItem>
            <ListItem
              sx={{
                paddingLeft: 0.5,
              }}
            >
              <ListItemText
                primary={"Device ID"}
                primaryTypographyProps={{
                  variant: "body2",
                }}
                secondaryTypographyProps={{
                  component: "span",
                }}
                secondary={
                  <DatasetDevice
                    datasetId={datasetId}
                    currentDeviceId={dataset.device_id || null}
                    org={currentOrg}
                    onError={setError}
                  />
                }
              />
            </ListItem>
            <ListItem
              sx={{
                paddingLeft: 0.5,
              }}
            >
              <ListItemText
                primary={"Modified"}
                primaryTypographyProps={{
                  variant: "body2",
                }}
                secondaryTypographyProps={{
                  component: "span",
                }}
                secondary={
                  <div
                    style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}
                  >
                    <RobotoTypography variant="body2">
                      <FormattedDateTime value={dataset.modified || ""} /> by{" "}
                    </RobotoTypography>
                    <RobotoPrincipalUnderlineLink
                      principal={dataset.modified_by}
                    />
                  </div>
                }
              />
            </ListItem>
            <ListItem
              sx={{
                paddingLeft: 0.5,
              }}
            >
              <ListItemText
                primary={"Tags"}
                primaryTypographyProps={{
                  variant: "body2",
                }}
                secondaryTypographyProps={{
                  component: "span",
                  mt: theme.spacing(1),
                }}
                secondary={
                  <UpdateDatasetTags
                    currentOrg={currentOrg}
                    datasetId={datasetId}
                    initialTags={dataset.tags}
                    onError={setError}
                  />
                }
              />
            </ListItem>
            <ListItem
              sx={{
                paddingLeft: 0.5,
              }}
            >
              <ListItemText
                primary={"Metadata"}
                primaryTypographyProps={{
                  variant: "body2",
                }}
                secondaryTypographyProps={{
                  component: "span",
                }}
                secondary={
                  <UpdateDatasetMetadata
                    currentOrg={currentOrg}
                    datasetId={datasetId}
                    metadata={dataset.metadata}
                    onError={setError}
                  />
                }
              />
            </ListItem>
            <ListItem sx={{ display: "block", paddingLeft: 0.5 }}>
              <EntityCustomFields
                entityType={TargetEntityType.Dataset}
                orgId={dataset.org_id}
                values={dataset.custom_fields}
                commit={(changeset) =>
                  updateDataset({
                    datasetId,
                    resourceOwnerId: dataset.org_id,
                    updates: { custom_fields_changeset: changeset },
                  })
                }
                onError={setError}
                variant="plain"
              />
            </ListItem>
            <ListItem
              sx={{
                paddingLeft: 0.5,
              }}
            >
              <ListItemText
                primary={"Operations"}
                primaryTypographyProps={{
                  variant: "body2",
                }}
                secondaryTypographyProps={{
                  component: "span",
                }}
                secondary={
                  <Box sx={{ mt: theme.spacing(1) }}>
                    <RobotoButton
                      eventName={"DeleteDatasetClicked"}
                      eventProperties={{
                        datasetId,
                      }}
                      variant={"outlined"}
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() =>
                        wrapActionWithDialog(
                          `Are you sure you want to delete dataset ${datasetId}?`,
                          function deleteDataset() {
                            return datasetService
                              .deleteDataset(datasetId, {
                                resourceOwnerId: currentOrg?.org_id,
                              })
                              .then(() => {
                                setError(null);
                                goto.search();
                              })
                              .catch((error: unknown) => {
                                const err =
                                  error instanceof Error
                                    ? error
                                    : new Error(String(error));
                                setError(err);
                              });
                          },
                        )
                      }
                    >
                      Delete
                    </RobotoButton>
                  </Box>
                }
              />
            </ListItem>
          </List>
        </div>

        <AlertDialog
          dialogOpen={alertDialogOpen}
          handleClose={() => {
            setAlertDialogOpen(false);
          }}
          dialogTitle={"Delete Dataset"}
          dialogText={alertDialogText}
          dialogAction={alertDialogAction}
        />
      </>
    );
  }

  return <CircularProgress size="1.5rem" />;
};
